import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import TablePagination from '@mui/material/TablePagination';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateTimeEntryDto, StartTimerDto, UpdateTimeEntryDto } from 'api';
import { projectService, timeEntryService } from 'api';
import { getErrorMessage, isApiError } from 'api/utils';
import dayjs from 'dayjs';
import { useAuthStore } from 'features/auth/stores/authStore';
import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';
import { queryKeys } from 'shared/constants/queryKeys';

import type { TimeEntry } from '../../../api/types/timeEntry.types';
import { formatDuration } from '../../../shared/lib/formatDuration';
import {
  EntriesColumn,
  EntriesScrollBox,
  EntryListMessage,
  TrackerLayout,
  WeekDateRange,
  WeekGroupCard,
  WeekGroupHeader,
  WeekLabel,
  WeekNavRow,
  WeekTotalTime,
} from '../tracker.styles';
import {
  addMonths,
  endOfMonth,
  formatWeekRange,
  groupByDay,
  groupByWeek,
  isCurrentMonth,
  monthLabel,
  startOfMonth,
  toDateStr,
} from '../utils/trackerUtils';

import { TimeEntryDayGroup } from './TimeEntryDayGroup';
import { TimerBar } from './TimerBar';

// ─── TrackerPage ──────────────────────────────────────────────────────────────

type TrackingMode = 'live' | 'manual';

const TrackerPage: React.FC = () => {
  const { user } = useAuthStore();
  const [mode, setMode] = useState<TrackingMode>('live');
  const [monthStart, setMonthStart] = useState<Date>(() => startOfMonth(new Date()));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(null);
  const [manualResetKey, setManualResetKey] = useState(0);

  const fromStr = toDateStr(startOfMonth(monthStart));
  const toStr = toDateStr(endOfMonth(monthStart));

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // ── Queries ────────────────────────────────────────────────────────────────

  const dateFilters = mode === 'manual' ? { from: fromStr, to: toStr } : {};

  const { data: pagedResult, isLoading: isLoadingEntries } = useQuery({
    queryKey: queryKeys.timeEntries.list({ ...dateFilters, page, rowsPerPage }),
    queryFn: () =>
      timeEntryService
        .getTimeEntries({ ...dateFilters, page, pageSize: rowsPerPage })
        .then((r) => r.data.data),
  });

  const { data: runningTimer = null } = useQuery({
    queryKey: queryKeys.timeEntries.current,
    queryFn: () => timeEntryService.getRunningTimer().then((r) => r.data.data),
    select: (data) => (data ? { ...data, clientReceivedAt: Date.now() } : null),
    staleTime: 29_000,
    refetchInterval: 30_000,
  });

  const weekGroups = useMemo(() => {
    const entries = (pagedResult?.data ?? []).filter(
      (e) => !!e.endedAt && (!runningTimer || e.id !== runningTimer.timeEntryId),
    );
    return groupByWeek(groupByDay(entries));
  }, [pagedResult, runningTimer]);

  const { data: projects = [] } = useQuery({
    queryKey: queryKeys.projects.accessible(),
    queryFn: () => projectService.getAccessibleProjects().then((r) => r.data.data),
  });

  // const { data: summary } = useQuery({
  //   queryKey: queryKeys.timeEntries.summary,
  //   queryFn: () => timeEntryService.getTimeSummary().then((r) => r.data.data),
  // });

  // ── Mutations ──────────────────────────────────────────────────────────────

  const startMutation = useMutation({
    mutationFn: (data: StartTimerDto) => timeEntryService.startTimer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.current });
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
    },
    onError: (error: unknown) =>
      enqueueSnackbar(isApiError(error) ? getErrorMessage(error) : 'Failed to start timer', {
        variant: 'error',
      }),
  });

  const stopMutation = useMutation({
    mutationFn: (timeEntryId: string) => timeEntryService.stopTimer({ timeEntryId }),
    onSuccess: () => {
      queryClient.setQueryData(queryKeys.timeEntries.current, null);
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.current });
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.summary });
    },
    onError: (error: unknown) => {
      const apiError = error as { status?: number; message?: string };
      if (apiError?.status === 409) {
        queryClient.setQueryData(queryKeys.timeEntries.current, null);
        queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.current });
        queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.summary });
        enqueueSnackbar(apiError.message ?? 'Timer was already stopped.', { variant: 'info' });
        return;
      }
      enqueueSnackbar(isApiError(error) ? getErrorMessage(error) : 'Failed to stop timer', {
        variant: 'error',
      });
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateTimeEntryDto) => timeEntryService.createTimeEntry(data),
    onSuccess: () => {
      setManualResetKey((k) => k + 1);
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.summary });
      enqueueSnackbar('Time entry added', { variant: 'success' });
    },
    onError: (error: unknown) =>
      enqueueSnackbar(isApiError(error) ? getErrorMessage(error) : 'Failed to add time entry', {
        variant: 'error',
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => timeEntryService.deleteTimeEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.summary });
      enqueueSnackbar('Entry deleted', { variant: 'success' });
    },
    onError: (error: unknown) =>
      enqueueSnackbar(isApiError(error) ? getErrorMessage(error) : 'Failed to delete entry', {
        variant: 'error',
      }),
  });

  const discardMutation = useMutation({
    mutationFn: (id: string) => timeEntryService.deleteTimeEntry(id),
    onSuccess: () => {
      queryClient.setQueryData(queryKeys.timeEntries.current, null);
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.current });
      enqueueSnackbar('Timer discarded', { variant: 'info' });
    },
    onError: (error: unknown) =>
      enqueueSnackbar(isApiError(error) ? getErrorMessage(error) : 'Failed to discard timer', {
        variant: 'error',
      }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTimeEntryDto }) =>
      timeEntryService.updateTimeEntry(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.current });
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.summary });
      enqueueSnackbar('Entry saved', { variant: 'success' });
    },
    onError: (error: unknown) =>
      enqueueSnackbar(isApiError(error) ? getErrorMessage(error) : 'Failed to update entry', {
        variant: 'error',
      }),
  });

  // Silent mutation for batch items — no per-item side effects.
  const batchItemMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTimeEntryDto }) =>
      timeEntryService.updateTimeEntry(id, data),
  });

  // Sequential batch update: runs each call one-by-one to prevent concurrent
  // upsert conflicts on report_daily_entry, then refreshes data exactly once.
  const handleBatchUpdate = async (updates: Array<{ id: string; data: UpdateTimeEntryDto }>) => {
    try {
      for (const { id, data } of updates) {
        await batchItemMutation.mutateAsync({ id, data });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.current });
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.summary });
      enqueueSnackbar('Entries saved', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(isApiError(e) ? getErrorMessage(e) : 'Failed to update entries', {
        variant: 'error',
      });
    }
  };

  // ── Grouped entries ────────────────────────────────────────────────────────

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleAddManual = (data: {
    projectId: string;
    description?: string;
    startedAt: string;
    endedAt: string;
    isBillable: boolean;
  }) => {
    createMutation.mutate({
      projectId: data.projectId,
      description: data.description,
      startedAt: data.startedAt,
      endedAt: data.endedAt,
      isBillable: data.isBillable,
    });
  };

  const handleUpdateRunningTimerDescription = (description: string, isBillable: boolean) => {
    if (!runningTimer?.projectId) return;
    updateMutation.mutate({
      id: runningTimer.timeEntryId,
      data: {
        projectId: runningTimer.projectId,
        description: description.trim() || undefined,
        startedAt: runningTimer.startedAt,
        isBillable,
      },
    });
  };

  const handleUpdateRunningTimerStartTime = (startedAt: string, isBillable: boolean) => {
    if (!runningTimer?.projectId) return;
    updateMutation.mutate({
      id: runningTimer.timeEntryId,
      data: {
        projectId: runningTimer.projectId,
        description: runningTimer.description,
        startedAt,
        isBillable,
      },
    });
  };

  const handleContinueEntry = (entry: TimeEntry) => {
    startMutation.mutate({
      projectId: entry.projectId ?? '',
      description: entry.description,
      taskId: entry.taskId,
      isBillable: entry.isBillable,
    });
  };

  const handleDuplicateEntry = (entry: TimeEntry) => {
    const endedAt =
      entry.endedAt ?? new Date(new Date(entry.startedAt).getTime() + 60 * 60 * 1000).toISOString();
    createMutation.mutate({
      projectId: entry.projectId ?? '',
      description: entry.description,
      startedAt: entry.startedAt,
      endedAt,
      isBillable: entry.isBillable,
    });
  };

  const prevMonth = () => {
    setMonthStart((m) => addMonths(m, -1));
    setPage(0);
  };
  const nextMonth = () => {
    setMonthStart((m) => addMonths(m, 1));
    setPage(0);
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div>
      <TimerBar
        mode={mode}
        projects={projects}
        runningTimer={runningTimer}
        onStart={(data) => startMutation.mutate(data)}
        onStop={(timeEntryId) => stopMutation.mutate(timeEntryId)}
        onDiscard={(timeEntryId) => discardMutation.mutate(timeEntryId)}
        onAddManual={handleAddManual}
        onUpdateDescription={handleUpdateRunningTimerDescription}
        onUpdateStartTime={handleUpdateRunningTimerStartTime}
        onModeChange={(m) => { setMode(m); setPage(0); }}
        isStarting={startMutation.isPending}
        isStopping={stopMutation.isPending}
        resetKey={manualResetKey}
        userName={user?.name?.split(' ')[0]}
      />

      {mode === 'manual' && (
        <WeekNavRow automargin={1}>
          <IconButton size="small" onClick={(e) => setCalendarAnchor(e.currentTarget)}>
            <CalendarTodayIcon fontSize="small" />
          </IconButton>
          <Popover
            open={Boolean(calendarAnchor)}
            anchorEl={calendarAnchor}
            onClose={() => setCalendarAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                value={dayjs(monthStart)}
                onChange={(newValue) => {
                  if (newValue) {
                    setMonthStart(startOfMonth(newValue.toDate()));
                    setPage(0);
                    setCalendarAnchor(null);
                  }
                }}
              />
            </LocalizationProvider>
          </Popover>
          <IconButton size="small" onClick={prevMonth}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <WeekLabel>
            {isCurrentMonth(monthStart) ? 'This Month' : monthLabel(monthStart)}
          </WeekLabel>
          <IconButton size="small" onClick={nextMonth} disabled={isCurrentMonth(monthStart)}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </WeekNavRow>
      )}

      <TrackerLayout>
        <EntriesColumn>
          <EntriesScrollBox>
            {isLoadingEntries && <EntryListMessage>Loading entries…</EntryListMessage>}
            {!isLoadingEntries && weekGroups.length === 0 && (
              <EntryListMessage>No time entries for this period.</EntryListMessage>
            )}
            {weekGroups.map((week) => (
              <React.Fragment key={week.weekStart}>
                <WeekGroupCard>
                  <WeekGroupHeader>
                    <WeekDateRange>{formatWeekRange(week.weekStart, week.weekEnd)}</WeekDateRange>
                    <WeekTotalTime>
                      Week total&nbsp;&nbsp;{formatDuration(week.totalSecs)}
                    </WeekTotalTime>
                  </WeekGroupHeader>
                  {week.dayGroups.map((group) => (
                    <TimeEntryDayGroup
                      key={group.date}
                      group={group}
                      projects={projects}
                      onDelete={(id) => deleteMutation.mutate(id)}
                      onDuplicate={handleDuplicateEntry}
                      onUpdate={(id, data) => updateMutation.mutate({ id, data })}
                      onUpdateBatch={handleBatchUpdate}
                      onContinue={handleContinueEntry}
                    />
                  ))}
                </WeekGroupCard>
              </React.Fragment>
            ))}

            {/* pagination */}
            <TablePagination
              component="div"
              count={pagedResult?.total ? pagedResult.total : 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </EntriesScrollBox>
        </EntriesColumn>

        {/* <WeeklySummary summary={summary} /> */}
      </TrackerLayout>
    </div>
  );
};

export default TrackerPage;
